/**
 * Pricing API client for credit pricing management
 */

if (!import.meta.env.VITE_BACKEND_URL) {
  throw new Error('VITE_BACKEND_URL environment variable is required');
}
const API_BASE_URL = import.meta.env.VITE_BACKEND_URL;

// ======================================
// Types
// ======================================

export interface CreditPricingRule {
  id: string;
  rule_name: string;
  user_type: 'individual' | 'practitioner' | 'all';
  activity_type: 'audio_generation' | 'shorts_generation';
  tier: 'free' | 'basic' | 'pro' | null;
  credits_per_minute?: number;
  credits_per_unit?: number;
  is_active: boolean;
  effective_from: string;
  updated_by?: string;
  description?: string;
  created_at: string;
  updated_at: string;
}

export interface SubscriptionTier {
  id: string;
  tier_name: string;
  user_type: 'individual' | 'practitioner';
  tier_level: 'free' | 'basic' | 'pro';
  monthly_price: number;
  monthly_credits_included: number;
  signup_bonus_credits: number;
  overage_rate_multiplier: number;
  features: string[];
  stripe_price_id?: string;
  is_active: boolean;
  display_order: number;
  created_at: string;
  updated_at: string;
}

export interface CreditCalculationRequest {
  activity_type: 'audio_generation' | 'shorts_generation';
  duration_minutes?: number;
}

export interface CreditCalculationResponse {
  credits_required: number;
  rate: number;
  user_type: string;
  tier: string;
  activity_type: string;
  duration_minutes?: number;
}

export interface PricingRatesResponse {
  user_type: string;
  tier: string;
  audio_rate: number;
  shorts_rate: number;
  overage_multiplier: number;
}

export interface CreditPricingRuleUpdate {
  credits_per_minute?: number;
  credits_per_unit?: number;
  is_active?: boolean;
  description?: string;
}

export interface SubscriptionTierUpdate {
  tier_name?: string;
  monthly_price?: number;
  monthly_credits_included?: number;
  signup_bonus_credits?: number;
  overage_rate_multiplier?: number;
  features?: string[];
  stripe_price_id?: string;
  is_active?: boolean;
  display_order?: number;
}

// ======================================
// Helper Functions
// ======================================

async function fetchWithAuth(url: string, options: RequestInit = {}): Promise<Response> {
  // Use cookie-based authentication 
  const response = await fetch(`${API_BASE_URL}${url}`, {
    ...options,
    credentials: 'include', // Include cookies for authentication
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Unknown error' }));
    throw new Error(error.detail || `HTTP ${response.status}: ${response.statusText}`);
  }

  return response;
}

// ======================================
// Public Pricing API
// ======================================

/**
 * Get current pricing rates for the authenticated user
 */
export async function getPricingRates(): Promise<PricingRatesResponse> {
  const response = await fetchWithAuth('/pricing/rates');
  return response.json();
}

/**
 * Calculate credits required for an activity
 */
export async function calculateCredits(request: CreditCalculationRequest): Promise<CreditCalculationResponse> {
  const response = await fetchWithAuth('/pricing/calculate', {
    method: 'POST',
    body: JSON.stringify(request),
  });
  return response.json();
}

// ======================================
// Admin Pricing API
// ======================================

/**
 * Get all pricing rules (admin only)
 */
export async function getAllPricingRules(): Promise<CreditPricingRule[]> {
  const response = await fetchWithAuth('/pricing/admin/rules');
  return response.json();
}

/**
 * Update a pricing rule (admin only, requires password)
 */
export async function updatePricingRule(
  ruleId: string,
  update: CreditPricingRuleUpdate,
  password: string
): Promise<CreditPricingRule> {
  const response = await fetchWithAuth(`/pricing/admin/rules/${ruleId}`, {
    method: 'PUT',
    body: JSON.stringify({
      rule_update: update,
      password_confirmation: { password }
    }),
  });
  return response.json();
}

/**
 * Reload pricing cache (admin only)
 */
export async function reloadPricingCache(): Promise<{ message: string }> {
  const response = await fetchWithAuth('/pricing/admin/reload', {
    method: 'POST',
  });
  return response.json();
}

/**
 * Get all subscription tiers (admin only)
 */
export async function getAllSubscriptionTiers(): Promise<SubscriptionTier[]> {
  const response = await fetchWithAuth('/pricing/admin/tiers');
  return response.json();
}

/**
 * Update a subscription tier (admin only, requires password)
 */
export async function updateSubscriptionTier(
  tierId: string,
  update: SubscriptionTierUpdate,
  password: string
): Promise<SubscriptionTier> {
  const response = await fetchWithAuth(`/pricing/admin/tiers/${tierId}`, {
    method: 'PUT',
    body: JSON.stringify({
      tier_update: update,
      password_confirmation: { password }
    }),
  });
  return response.json();
}
